import { useState } from 'react';

import usePromoState from 'application/hooks/usePromoState';
import { ADS_PAGE_PATH } from 'application/pages/AdsPage';
import { usePlayerContext } from 'application/providers/PlayerProvider';
import { RouterUtils, useNavigate } from 'application/providers/RouterProvider';
import AnalyticsService, { EventNameAMP, ParamNameAMP } from 'application/services/AnalyticsService';
import { useAccountCompanyId } from 'application/hooks';
import { DEFAULT_ERROR_MESSAGE } from '@shared/config';

import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';
import { MediaUploaderItemType, MediaUploaderItemValue } from '@zvuk-b2b/react-uikit/ui/MediaUploader';
import { AdCampaignMediaTypeEnum } from '@shared/api/graphql/generated';
import { toast } from '@shared/ui';

import FileUtils from 'application/utils/FileUtils';
import { NAME_MAX_LENGTH_ERROR, NOTIFICATIONS_TEXT } from '../consts';
import { UseAdCreateModalControllerProps } from './types';

const useAdCreateModalController = ({
  close,
  onCreate,
  createAdCampaign,
  clearAdCampaigns,
}: UseAdCreateModalControllerProps) => {
  const companyId = useAccountCompanyId();

  const { isDesktop } = useMedia();
  const navigate = useNavigate();
  const playerContext = usePlayerContext();

  const { data, upload, remove, retry, clear } = usePromoState();

  const [adName, setAdName] = useState('');

  const createModalItems = data.statuses.map<MediaUploaderItemType>((item) => ({
    label: item.file?.name || item.title || '',
    isLoading: item.isLoading,
    error: item.error,
    value: {
      id: item.id || '',
      filename: item.filename || '',
      url: FileUtils.getMediafileUrl(item.sourcefiles),
      duration: item.duration || undefined,
      extension: item.sourcefiles ? item?.sourcefiles[0]?.extension : undefined,
      imgLink: item.previewUrl,
    },
  }));

  const isAnyItemLoading = createModalItems.some((item) => item.isLoading);

  const onFilesSelected = (files: File[]) => {
    upload(files, AdCampaignMediaTypeEnum.Audio);
  };

  const onRemoveFile = (value: MediaUploaderItemValue) => {
    remove(value.id);
  };

  const onRetryFile = (value: MediaUploaderItemValue) => {
    retry(value.id);
  };

  const onPlayClick = (value: MediaUploaderItemValue) => {
    playerContext.play({ fileUuid: value.id, fileName: value.filename, fileUrl: value.url });
  };

  const onStopClick = (_: MediaUploaderItemValue) => {
    playerContext.stop();
  };

  const onRemoveClick = (value: MediaUploaderItemValue) => {
    if (playerContext.fileName === value.filename) {
      playerContext.stop();
    }

    onRemoveFile(value);
  };

  const onAdCampaignCreateClick = async (name: string, promoIds: string[]) => {
    try {
      const { id, type } = await createAdCampaign(name, promoIds, companyId, {
        mediaType: AdCampaignMediaTypeEnum.Audio,
      });

      if (id) {
        toast.info(NOTIFICATIONS_TEXT.CREATE_SUCCESS);
        AnalyticsService.event(EventNameAMP.AD_CAMPAIGN_CREATED, {
          [ParamNameAMP.AD_CAMPAIGN_ID]: id,
          [ParamNameAMP.AD_CAMPAIGN_TYPE]: type,
          [ParamNameAMP.FLOW]: 'default',
        });

        if (isDesktop) {
          navigate(RouterUtils.generatePath(ADS_PAGE_PATH, { id }));
        }

        clearAdCampaigns();
      } else {
        throw Error();
      }
    } catch (error: any) {
      if (JSON.stringify(error).includes(NAME_MAX_LENGTH_ERROR)) {
        toast.error(NOTIFICATIONS_TEXT.MAX_LENGTH_ERROR);
      } else {
        toast.error(DEFAULT_ERROR_MESSAGE);
      }
    }

    clear();
    close();
  };

  const onSubmitClick = () => {
    playerContext.stop();
    const promoIds = data.statuses.map((item) => item.promoId!).filter(Boolean) || [];

    if (onCreate) {
      onCreate(adName, data.statuses);
      close();
    } else {
      onAdCampaignCreateClick(adName, promoIds);
    }
  };

  const onCancelClick = () => {
    playerContext.stop();
    close();
  };

  return {
    items: createModalItems,
    playerContext,
    adName,
    setAdName,
    isAnyItemLoading,
    onFilesSelected,
    onRetryFile,
    onSubmitClick,
    onCancelClick,
    onPlayClick,
    onStopClick,
    onRemoveClick,
  };
};

export default useAdCreateModalController;
